import {
    Button,
    ButtonGroup,
    Center,
    Flex,
    Icon,
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { Table } from '@tanstack/react-table';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { BiCog, BiMove } from 'react-icons/bi';
import { BsEye, BsEyeSlash } from 'react-icons/bs';

import { getColumnLabel } from '../../utils/tableUtils';

export const ColumnSettings = <T,>({ table }: { table: Table<T> }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();

    const columns = table.getAllLeafColumns().filter((column) => !column.columnDef.meta?.isDisplayCol);

    const moveColumn = (fromIdx: number, toIdx: number) => {
        const cols = [...columns];
        const [reorderedItem] = cols.splice(fromIdx, 1);
        cols.splice(toIdx, 0, reorderedItem);
        table.setColumnOrder(cols.map(({ id }) => id));
    };

    return (
        <>
            <Button leftIcon={<Icon as={BiCog} />} size="sm" onClick={onOpen}>
                Table Settings
            </Button>

            <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Table settings</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <DragDropContext
                            onDragEnd={(result) => {
                                if (result.destination) {
                                    moveColumn(result.source.index, result.destination.index);
                                }
                            }}
                        >
                            <Droppable droppableId="droppable">
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                        {columns.map((column, idx) => (
                                            <Draggable key={column.id} draggableId={column.id} index={idx}>
                                                {(prov) => (
                                                    <Flex
                                                        align="center"
                                                        mb={1}
                                                        px={2}
                                                        py={1}
                                                        borderWidth={1}
                                                        borderRadius="md"
                                                        justify="space-between"
                                                        ref={prov.innerRef}
                                                        {...prov.draggableProps}
                                                    >
                                                        <Flex gap={3}>
                                                            <Center {...prov.dragHandleProps}>
                                                                <Icon as={BiMove} />
                                                            </Center>
                                                            <Text>{getColumnLabel(column.columnDef.id!)}</Text>
                                                        </Flex>
                                                        <IconButton
                                                            size="sm"
                                                            icon={
                                                                <Icon
                                                                    as={column.getIsVisible() ? BsEye : BsEyeSlash}
                                                                    color={
                                                                        column.getIsVisible() ? 'default' : 'red.300'
                                                                    }
                                                                />
                                                            }
                                                            aria-label="toggle column visibility"
                                                            onClick={(event) => {
                                                                column.getToggleVisibilityHandler()(event);
                                                                column.getIsVisible() &&
                                                                    moveColumn(idx, columns.length - 1);
                                                            }}
                                                        />
                                                    </Flex>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder as React.ReactNode}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </ModalBody>

                    <ModalFooter>
                        <ButtonGroup>
                            <Button
                                onClick={() => {
                                    table.resetColumnOrder();
                                    table.resetColumnVisibility();
                                }}
                            >
                                Reset
                            </Button>
                            <Button colorScheme="teal" onClick={onClose}>
                                Close
                            </Button>
                        </ButtonGroup>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};
